import React, { useEffect, useState } from 'react';
import {
    Box,
    Center,
    ChevronLeftIcon,
    FlatList,
    HStack,
    Pressable,
    VStack
} from 'native-base';
import {
    Product
} from '@springbig/shared/src/services/dutchie/schema-types';
import { useDutchieStore, useI18n, useMenuQuery } from '@springbig/shared';
import { useShopStyles } from '~/shared/styles';
import Section from '~/shared/Section';
import { Caption } from '~/shared/Typography';
import { useAppNavigate } from '..';
import { BrandsCard } from './components/BrandsCard';
import { ProductCard } from './components/ProductCard';
import { ShopHeader } from './components/ShopHeader';
import { useWindowSize } from 'usehooks-ts';
import { ActiveFiltersList } from './components/ActiveFiltersList';
import { MenuResponse } from '@springbig/shared';

export const ProductListPage = () => {

    const { retailer, menuType, productListProps } = useDutchieStore();
    const { mutate } = useMenuQuery();
    const { section_background, button_background } = useShopStyles();

    const [offset, setOffset] = useState(0);
    const [products, setProducts] = useState<Array<Product> | undefined>();
    const { filters, sortByProp } = useDutchieStore();
    const effect = filters?.effects;
    const strain = filters?.strainType;
    const menuSort = sortByProp?.menuSort;
    const limit = 10;
    const { navigate, params } = useAppNavigate();
    const { category, subCategory, menuSection, title, brand, search } = productListProps ?? {};
    const { width } = useWindowSize();
    const { t } = useI18n();

    const hasFiltersEnabled = !!filters?.strainType || filters?.effects?.length > 0 || (filters?.minPrice || filters?.maxPrice);

    const createVariables = () => ({
        retailerId: retailer?.id,
        menuType,
        filter: {
            search,
            category,
            subCategory,
            effects: effect,
            strainType: strain,
            menuSection: menuSection ? { type: menuSection.type, specialId: menuSection.specialId } : undefined,
            brandId: brand?.id,
        },
        sort: { direction: menuSort?.direction, key: menuSort?.key },
        pagination: { offset, limit }
    });

    const filterProductsByPrice = (oldProducts: Product[], newProducts: Product[]) => {


        if (filters?.minPrice !== undefined && filters?.maxPrice !== undefined) {
            const minValue = filters.minPrice;
            const maxValue = filters.maxPrice;
            return [...oldProducts, ...newProducts].filter(e => {

                const price = menuType === 'MEDICAL' ? e.variants[0].specialPriceMed ?? e.variants[0].priceMed ?? 0 : e.variants[0].specialPriceRec ?? e.variants[0].priceRec ?? 0;
                return price <= maxValue && price >= minValue;
            });
        }
    };

    useEffect(() => {

        if (retailer && menuType && menuSort) {
            const variables = createVariables();
            mutate(variables, { onSuccess: (data: MenuResponse) => {


                if (data && data.menu.products.length) {
                    if (filters?.minPrice || filters?.maxPrice) {
                        setProducts(prevState => filterProductsByPrice(prevState ?? [], data.menu.products));
                    }
                    else {
                        setProducts(prevState => (offset === 0 ? data.menu.products : [...(prevState ?? []), ...data.menu.products]));
                    }
                }
            } });
        }
    }, [
        productListProps,
        retailer,
        menuType,
        category,
        subCategory,
        effect,
        strain,
        menuSection,
        limit,
        menuSort,
        offset,
        search,
        filters?.minPrice,
        filters?.maxPrice,
    ]);

    useEffect(() => {

        setOffset(0);
        setProducts([]);
    }, [
        category,
        subCategory,
        effect,
        strain,
        menuSection,
        limit,
        menuSort,
        search,
        filters?.minPrice,
        filters?.maxPrice,
    ]);

    return (
        <Section>
            <ShopHeader />
            { hasFiltersEnabled ? <ActiveFiltersList t={t} /> : <></>}
            <VStack ml={2}>
                <HStack alignItems={'center'} justifyContent={'space-between'} space={4} p={2}>
                    <Pressable
                        onPress={() => navigate(`w/${params.wallet_id}/shop/`)}
                        bg={section_background}
                        p={1}
                        rounded={'md'}
                        position={'absolute'}
                    >
                        <ChevronLeftIcon size='lg' color={button_background} />
                    </Pressable>
                    { brand ?
                        <Center w={'100%'} overflow={'hidden'}>
                            <BrandsCard brand={brand} />
                        </Center>
                        :
                        <Box
                            bg={section_background}
                            py={1}
                            px={2}
                            borderRadius={4}
                            mx={'auto'}
                        >
                            <Caption>{ title }</Caption>
                        </Box>
                    }
                </HStack>
                { products?.length ?
                    <FlatList
                        _contentContainerStyle={ {  margin: 'auto' } }
                        horizontal={false}
                        numColumns={width < 425 ? 2 : 3}
                        data={products}
                        keyExtractor={(item : Product, index : number) => (`${item.id}-${index}-${item.name}`)}
                        renderItem={({ item, index }: { item: Product, index : number }) =>

                            <Box py={2}>
                                <ProductCard item={item} key={`${item.id}-${index}-${item.name}`} />
                            </Box>
                        }
                        onEndReached={({ distanceFromEnd }) => {

                            if (distanceFromEnd < 0) {
                                return;
                            }

                            setOffset(offset + limit);
                        }}
                        contentContainerStyle={{ flex: 1, margin: 'auto' }}
                        onEndReachedThreshold={0.8}
                    />
                    :
                    <></>
                }
            </VStack>
        </Section>
    );
};
